import React, { useState, useRef, useCallback, useEffect } from 'react'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import OrdersTooltip from '../../OrdersTooltip'
import {
  normalizeCustomerCode,
  validateCustomerCodeLocal
} from '../../../../../utils/customerValidation'
import {
  createTableInputResolver,
  getKeyboardNavigationDirection
} from '../utils/keyboardNavigation'
import { HEADER_FIELDS } from '../constants'
import './ColumnHeader.css'
import './ColumnValueFilter.css'
import ColumnValueFilter from './ColumnValueFilter'
import {
  getColumnType,
  getColumnTypeClass
} from '../../../../../utils/columnType'

const ColumnHeader = ({
  columns,
  allColumns = [],
  hiddenColumnKeys = new Set(),
  hiddenRowKeys = new Set(),
  hiddenHeaderFieldKeys = new Set(),
  onUnhideItem,
  selectedItems = [],
  getStickyLeft,
  buyerLeft,
  selectedColumn,
  selectedColumnIds,
  setSelectedColumn,
  setSelectedRow,
  handleColumnContextMenu,
  handleColumnSelection,
  handleMouseDown,
  newColumnId,
  newColumnInputRefs,
  hasAutoFocused,
  handleCustomerCodeChange,
  handleCustomerCodeEmptyLabel,
  orders,
  hasOriginalOrderQuantity,
  editingColumnId,
  setEditingColumnId,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  draggedColumnId,
  dropTargetId,
  isAtEnd,
  isReadOnly = false,
  stickyTop = 0,
  gradeFilterValues = [],
  sizeFilterValues = [],
  gradeFilterOptions = [],
  sizeFilterOptions = [],
  onGradeFilterChange,
  onSizeFilterChange,
  isFilterLoading = false
}) => {
  const [hoveredColumnKey, setHoveredColumnKey] = useState(null)
  const hideTimerRef = useRef(null)
  const tooltipLockRef = useRef(false)
  const controlKeyRefs = useRef(new Map())
  const [openFilter, setOpenFilter] = useState(null)
  const filterButtonRefs = useRef({})

  // When a column is removed, the adjacent column shifts into the cursor's position
  // and the browser fires mouseenter on it automatically. Lock tooltip briefly to
  // prevent this phantom hover from showing a stale/unexpected tooltip.
  useEffect(() => {
    const unlock = () => {
      tooltipLockRef.current = true
      setHoveredColumnKey(null)
      clearTimeout(hideTimerRef.current)
      const t = setTimeout(() => {
        tooltipLockRef.current = false
      }, 300)
      return t
    }
    const unsubscribe = eventBus.on(EVENTS.COLUMN_REMOVED, unlock)
    return () => unsubscribe()
  }, [])

  // Clear stale hover state when the hovered column is removed from the list
  useEffect(() => {
    if (
      hoveredColumnKey &&
      !columns.some((col) => col.customerCode === hoveredColumnKey)
    ) {
      clearTimeout(hideTimerRef.current)
      setHoveredColumnKey(null)
    }
  }, [columns, hoveredColumnKey])

  const ensureVisible = (element) => {
    const scrollContainer = element.closest('.rp-table-scroll')
    if (!scrollContainer) return

    const rect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    const stickyCols = Array.from(
      scrollContainer.querySelectorAll('.sticky-col')
    )
    let stickyWidth = 0
    if (stickyCols.length > 0) {
      const lastSticky = stickyCols[stickyCols.length - 1]
      const lastStickyRect = lastSticky.getBoundingClientRect()
      stickyWidth = lastStickyRect.right - containerRect.left
    }

    if (rect.left < containerRect.left + stickyWidth) {
      scrollContainer.scrollLeft -=
        containerRect.left + stickyWidth - rect.left + 20
    } else if (rect.right > containerRect.right) {
      scrollContainer.scrollLeft += rect.right - containerRect.right + 20
    }
  }

  const findNextInput = (currentInput, direction) => {
    const currentTd = currentInput.closest('td, th')
    if (!currentTd) return null
    const currentTr = currentTd.closest('tr')
    if (!currentTr) return null

    const allTrs = Array.from(document.querySelectorAll('.rp-table tr'))
    const currentTrIdx = allTrs.indexOf(currentTr)

    const isAutocomplete = (el) => el.closest('.MuiAutocomplete-root')

    if (direction === 'up' || direction === 'down') {
      const step = direction === 'up' ? -1 : 1
      const currentRect = currentTd.getBoundingClientRect()
      const currentCenter = currentRect.left + currentRect.width / 2

      for (
        let i = currentTrIdx + step;
        i >= 0 && i < allTrs.length;
        i += step
      ) {
        const row = allTrs[i]
        const cells = Array.from(row.cells)

        const targetTd = cells.find((td) => {
          const rect = td.getBoundingClientRect()
          return currentCenter >= rect.left && currentCenter <= rect.right
        })

        if (targetTd) {
          const input = targetTd.querySelector('input')
          if (
            input &&
            !input.disabled &&
            !input.readOnly &&
            !isAutocomplete(input)
          ) {
            return input
          }
        }
      }
    } else if (direction === 'left' || direction === 'right') {
      const allInputs = Array.from(
        document.querySelectorAll('.rp-table input')
      ).filter((i) => !i.disabled && !i.readOnly && !isAutocomplete(i))

      const currentIndex = allInputs.indexOf(currentInput)
      if (direction === 'left' && currentIndex > 0)
        return allInputs[currentIndex - 1]
      if (direction === 'right' && currentIndex < allInputs.length - 1)
        return allInputs[currentIndex + 1]
    }
    return null
  }

  const handleMouseEnter = useCallback((customerCode) => {
    if (tooltipLockRef.current) return
    clearTimeout(hideTimerRef.current)
    setHoveredColumnKey(customerCode)
  }, [])

  const handleMouseLeave = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setHoveredColumnKey(null)
    }, 120)
  }, [])

  return (
    <>
      {columns.map((col, idx) => {
        // The visible 階級 column is named `class` in ColumnManager while the
        // underlying row field remains `size`.
        const filterField =
          col.id === 'grade' ? 'grade' : col.id === 'class' ? 'size' : null
        const filterValues = filterField === 'grade' ? gradeFilterValues : sizeFilterValues
        const filterOptions = filterField === 'grade' ? gradeFilterOptions : sizeFilterOptions
        const isCustomer =
          col.type === 'customer' || col.type === 'black-circle'
        const isFixedColumn = !isCustomer
        const isLastFixedColumn = col.id === 'branch'
        const stickyStyle = isFixedColumn
          ? {
              left: `${col.id === 'branch' ? buyerLeft : getStickyLeft(idx)}px`,
              zIndex: 1000
            }
          : { zIndex: 103 }

        // Check if there are hidden columns BEFORE this one
        let hiddenBefore = []
        const currentAllIdx = allColumns.findIndex((ac) => ac.id === col.id)
        if (currentAllIdx !== -1) {
          for (let i = currentAllIdx - 1; i >= 0; i--) {
            const prevCol = allColumns[i]
            if (hiddenColumnKeys.has(prevCol.customerCode)) {
              hiddenBefore.push(prevCol.customerCode)
            } else {
              break
            }
          }
        }

        // Check if there are hidden columns AFTER this one
        let hiddenAfter = []
        if (currentAllIdx !== -1) {
          for (let i = currentAllIdx + 1; i < allColumns.length; i++) {
            const nextCol = allColumns[i]
            if (hiddenColumnKeys.has(nextCol.customerCode)) {
              hiddenAfter.push(nextCol.customerCode)
            } else {
              break
            }
          }
        }

        const colType = isCustomer
          ? getColumnType(col.customerCode, orders, hasOriginalOrderQuantity)
          : 'deletable'

        const colTypeClass = getColumnTypeClass(colType)

        return (
          <th
            key={col.id}
            data-column-id={col.id}
            className={`${
              col.type === 'row-number' ? 'row-number' : 'header-cell'
            } sticky-header ${isLastFixedColumn ? 'sticky-shadow' : ''} ${
              isFixedColumn ? 'sticky-col' : ''
            } ${
              isCustomer ? 'customer-header' : ''
            } ${colTypeClass} ${
              selectedColumnIds.includes(col.id) ? 'selected-column-header' : ''
            } ${dropTargetId === col.id ? (isAtEnd ? 'drag-over-right' : 'drag-over') : ''} group`}
            style={{
              width: `${col.width}px`,
              minWidth: `${col.width}px`,
              whiteSpace: String(col.label ?? '').includes('\n')
                ? 'pre-line'
                : 'nowrap',
              top: `${stickyTop}px`,
              ...stickyStyle
            }}
            onClick={(e) => {
              if (isCustomer) {
                handleColumnSelection(
                  col.id,
                  e,
                  allColumns.map((column) => column.id)
                )
                setSelectedColumn(col.customerCode)
                setSelectedRow(null)
              }
            }}
            onDoubleClick={() => {
              if (
                !isReadOnly &&
                isCustomer &&
                !col.customerCode.startsWith('NEW_') &&
                colType === 'deletable'
              ) {
                setEditingColumnId(col.customerCode)
              }
            }}
            onContextMenu={
              isCustomer && !isReadOnly
                ? (e) => handleColumnContextMenu(e, col.customerCode, col.id)
                : undefined
            }
            onMouseEnter={
              isCustomer ? () => handleMouseEnter(col.customerCode) : undefined
            }
            onMouseLeave={isCustomer ? handleMouseLeave : undefined}
            draggable={!isReadOnly && !col.fixed}
            onDragStart={
              isReadOnly ? undefined : (e) => handleDragStart(e, col.id)
            }
            onDragEnd={isReadOnly ? undefined : handleDragEnd}
            onDragOver={
              isReadOnly ? undefined : (e) => handleDragOver(e, col.id)
            }
            onDragLeave={isReadOnly ? undefined : handleDragLeave}
            onDrop={isReadOnly ? undefined : (e) => handleDrop(e, col.id)}
          >
            {col.type === 'row-number' &&
              hiddenHeaderFieldKeys.has(
                HEADER_FIELDS[HEADER_FIELDS.length - 1].key
              ) && (
                <div
                  className='absolute top-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isReadOnly) return
                    HEADER_FIELDS.forEach((f) => {
                      if (hiddenHeaderFieldKeys.has(f.key)) {
                        onUnhideItem('headerField', f.key)
                      }
                    })
                  }}
                  title='隠れているヘッダー行を表示'
                >
                  <svg
                    width='8'
                    height='8'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M7 11l5-5 5 5M7 18l5-5 5 5' />
                  </svg>
                </div>
              )}

            {col.type === 'row-number' &&
              selectedItems.length > 0 &&
              hiddenRowKeys.has(
                `${selectedItems[0].hinmeiCode}::${selectedItems[0].niukabango}`
              ) && (
                <div
                  className='absolute bottom-0 left-0 right-0 h-2 flex items-center justify-center bg-blue-100 hover:bg-blue-300 transition-colors z-10 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isReadOnly) return
                    selectedItems.forEach((item) => {
                      const rowKey = `${item.hinmeiCode}::${item.niukabango}`
                      if (hiddenRowKeys.has(rowKey)) {
                        onUnhideItem('row', rowKey)
                      }
                    })
                  }}
                  title='隠れている商品行を表示'
                >
                  <svg
                    width='8'
                    height='8'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M7 13l5 5 5-5M7 6l5 5 5-5' />
                  </svg>
                </div>
              )}

            {hiddenBefore.length > 0 && (
              <div
                className='absolute left-0 top-0 bottom-0 w-2 flex items-center justify-center bg-[#106EBE] hover:bg-blue-400 transition-colors z-20 cursor-pointer'
                onClick={(e) => {
                  e.stopPropagation()
                  if (isReadOnly) return
                  hiddenBefore.forEach((k) => onUnhideItem('column', k))
                }}
                title='隠れている列を表示'
              >
                <svg
                  width='8'
                  height='8'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M11 17l-5-5 5-5M18 17l-5-5 5-5' />
                </svg>
              </div>
            )}

            {isCustomer &&
              !col.customerCode.startsWith('NEW_') &&
              col.label &&
              hoveredColumnKey === col.customerCode &&
              (newColumnInputRefs.current[col.customerCode]?.value ??
                col.label) !== '' &&
              orders?.length > 0 && (
                <OrdersTooltip
                  orders={orders}
                  columnKey={col.customerCode}
                  onMouseEnter={() => {
                    clearTimeout(hideTimerRef.current)
                  }}
                  onMouseLeave={() => {
                    hideTimerRef.current = setTimeout(() => {
                      setHoveredColumnKey(null)
                    }, 120)
                  }}
                />
              )}

            {isCustomer ? (
              <input
                ref={(el) => {
                  if (el) newColumnInputRefs.current[col.customerCode] = el
                  if (
                    el &&
                    !hasAutoFocused.current.has(col.customerCode) &&
                    (col.customerCode.startsWith('NEW_') ||
                      editingColumnId === col.customerCode)
                  ) {
                    hasAutoFocused.current.add(col.customerCode)
                    // Use col.label directly; if the user has intentionally
                    // cleared it to '', show empty rather than the raw key.
                    el.value = col.customerCode.startsWith('NEW_')
                      ? ''
                      : col.label
                    el.focus()
                    el.select()
                  }
                }}
                type='text'
                readOnly={isReadOnly || colType !== 'deletable'}
                defaultValue={
                  col.customerCode.startsWith('NEW_') ? '' : col.label
                }
                onBlur={async (e) => {
                  controlKeyRefs.current.delete(col.customerCode)
                  if (e.target.dataset.customerSubmit === '1') {
                    e.target.dataset.customerSubmit = '0'
                    hasAutoFocused.current.delete(col.customerCode)
                    setEditingColumnId(null)
                    return
                  }

                  const newValRaw = e.target.value.trim()
                  const normalized = normalizeCustomerCode(newValRaw)
                  if (normalized && normalized !== newValRaw) {
                    e.target.value = normalized
                  }

                  const fallbackValue = col.customerCode.startsWith('NEW_')
                    ? ''
                    : col.label

                  if (normalized) {
                    const localError = validateCustomerCodeLocal(normalized)
                    if (localError) {
                      alert(localError)
                      e.target.value = fallbackValue
                      setTimeout(() => {
                        e.target.focus()
                        e.target.select()
                      }, 0)
                      return
                    }
                  } else if (!col.customerCode.startsWith('NEW_')) {
                    // User cleared the input to empty — log and keep empty display
                    handleCustomerCodeEmptyLabel(col.customerCode)
                    hasAutoFocused.current.delete(col.customerCode)
                    setEditingColumnId(null)
                    return
                  }

                  if (normalized && normalized !== col.label) {
                    const success = await handleCustomerCodeChange(
                      col.customerCode,
                      normalized
                    )
                    if (success === false) {
                      e.target.value = fallbackValue
                      setTimeout(() => {
                        e.target.focus()
                        e.target.select()
                      }, 0)
                      return
                    }
                  }
                  if (!col.customerCode.startsWith('NEW_') || normalized) {
                    hasAutoFocused.current.delete(col.customerCode)
                  }
                  setEditingColumnId(null)
                }}
                onKeyDown={async (e) => {
                  const { key, target } = e
                  const controlState = controlKeyRefs.current.get(
                    col.customerCode
                  ) || { pending: false, combined: false }
                  if (key === 'Control') {
                    controlKeyRefs.current.set(col.customerCode, {
                      pending: true,
                      combined: false
                    })
                    return
                  }
                  if (e.ctrlKey || controlState.pending) {
                    if (controlState.pending) controlState.combined = true
                    controlKeyRefs.current.set(col.customerCode, controlState)
                    return
                  }
                  const keyboardDirection = getKeyboardNavigationDirection(key)
                  if (keyboardDirection) {
                    e.preventDefault()
                    const newValRaw = e.target.value.trim()
                    const normalized = normalizeCustomerCode(newValRaw)
                    if (normalized && normalized !== newValRaw) {
                      e.target.value = normalized
                    }

                    // Pre-check local validation to prevent double alert:
                    // alert() inside handleCustomerCodeChange triggers blur,
                    // then setTimeout below would cause a second blur → second alert.
                    if (normalized) {
                      const localError = validateCustomerCodeLocal(normalized)
                      if (localError) {
                        // flag='1' suppresses onBlur (which fires while alert blocks JS).
                        // After alert returns, re-focus via setTimeout so all pending
                        // blur/focus events finish first before we restore focus.
                        e.target.dataset.customerSubmit = '1'
                        const inputEl = e.target
                        const fallbackValue = col.customerCode.startsWith(
                          'NEW_'
                        )
                          ? ''
                          : col.label
                        alert(localError)
                        setTimeout(() => {
                          inputEl.value = fallbackValue
                          inputEl.dataset.customerSubmit = '0'
                          inputEl.focus()
                          inputEl.select()
                        }, 0)
                        return
                      }
                    }

                    const isClearingCustomerCode =
                      !normalized &&
                      !col.customerCode.startsWith('NEW_') &&
                      col.label !== ''
                    const nextInput = findNextInput(target, keyboardDirection)
                    const resolveNextInput =
                      (normalized && normalized !== col.label) ||
                      isClearingCustomerCode
                        ? createTableInputResolver(nextInput)
                        : () => nextInput

                    if (isClearingCustomerCode) {
                      target.dataset.customerSubmit = '1'
                      handleCustomerCodeEmptyLabel(col.customerCode)
                    } else if (normalized && normalized !== col.label) {
                      e.target.dataset.customerSubmit = '1'
                      const success = await handleCustomerCodeChange(
                        col.customerCode,
                        normalized
                      )
                      if (!success) {
                        e.target.dataset.customerSubmit = '0'
                        const fallbackValue = col.customerCode.startsWith(
                          'NEW_'
                        )
                          ? ''
                          : col.label
                        e.target.value = fallbackValue
                        setTimeout(() => {
                          e.target.focus()
                          e.target.select()
                        }, 0)
                        return
                      }
                    }

                    if (!col.customerCode.startsWith('NEW_') || normalized) {
                      hasAutoFocused.current.delete(col.customerCode)
                    }
                    setEditingColumnId(null)

                    if (nextInput) {
                      setTimeout(() => {
                        const currentNextInput = resolveNextInput()
                        if (!currentNextInput) return
                        currentNextInput.focus()
                        currentNextInput.select()
                        ensureVisible(currentNextInput)
                      }, 0)
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    target.value = col.label
                    if (!col.customerCode.startsWith('NEW_')) {
                      hasAutoFocused.current.delete(col.customerCode)
                    }
                    setEditingColumnId(null)
                    target.blur()
                  } else if (
                    [
                      'ArrowUp',
                      'ArrowDown',
                      'ArrowLeft',
                      'ArrowRight'
                    ].includes(key)
                  ) {
                    const isLeft = key === 'ArrowLeft'
                    const isRight = key === 'ArrowRight'
                    const isUp = key === 'ArrowUp'
                    const isDown = key === 'ArrowDown'

                    if (isLeft && target.selectionStart !== 0) return
                    if (isRight && target.selectionEnd !== target.value.length)
                      return

                    const direction = isUp
                      ? 'up'
                      : isDown
                        ? 'down'
                        : isLeft
                          ? 'left'
                          : 'right'
                    const nextInput = findNextInput(target, direction)

                    if (nextInput) {
                      e.preventDefault()
                      nextInput.focus()
                      nextInput.select()
                      ensureVisible(nextInput)
                    }
                  }
                }}
                onKeyUp={async (e) => {
                  if (e.key !== 'Control') return
                  const controlState = controlKeyRefs.current.get(
                    col.customerCode
                  )
                  controlKeyRefs.current.delete(col.customerCode)
                  if (
                    !controlState?.pending ||
                    controlState.combined ||
                    isReadOnly ||
                    colType !== 'deletable'
                  ) {
                    return
                  }

                  e.preventDefault()
                  const target = e.currentTarget
                  const newValRaw = target.value.trim()
                  const normalized = normalizeCustomerCode(newValRaw)
                  if (normalized && normalized !== newValRaw) {
                    target.value = normalized
                  }

                  if (normalized) {
                    const localError = validateCustomerCodeLocal(normalized)
                    if (localError) {
                      target.dataset.customerSubmit = '1'
                      const fallbackValue = col.customerCode.startsWith('NEW_')
                        ? ''
                        : col.label
                      alert(localError)
                      target.value = fallbackValue
                      target.dataset.customerSubmit = '0'
                      target.focus()
                      target.select()
                      return
                    }
                  }

                  const nextInput = findNextInput(target, 'left')
                  const isClearingCustomerCode =
                    !normalized &&
                    !col.customerCode.startsWith('NEW_') &&
                    col.label !== ''
                  const resolveNextInput =
                    (normalized && normalized !== col.label) ||
                    isClearingCustomerCode
                      ? createTableInputResolver(nextInput)
                      : () => nextInput
                  if (isClearingCustomerCode) {
                    target.dataset.customerSubmit = '1'
                    handleCustomerCodeEmptyLabel(col.customerCode)
                  } else if (normalized && normalized !== col.label) {
                    target.dataset.customerSubmit = '1'
                    const success = await handleCustomerCodeChange(
                      col.customerCode,
                      normalized
                    )
                    if (!success) {
                      target.dataset.customerSubmit = '0'
                      target.value = col.customerCode.startsWith('NEW_')
                        ? ''
                        : col.label
                      target.focus()
                      target.select()
                      return
                    }
                  }

                  if (!col.customerCode.startsWith('NEW_') || normalized) {
                    hasAutoFocused.current.delete(col.customerCode)
                  }
                  setEditingColumnId(null)
                  if (nextInput) {
                    setTimeout(() => {
                      const currentNextInput = resolveNextInput()
                      if (!currentNextInput) return
                      currentNextInput.focus()
                      currentNextInput.select()
                      ensureVisible(currentNextInput)
                    }, 0)
                  }
                }}
                onFocus={(e) => {
                  e.target.select()
                  eventBus.emit(EVENTS.CELL_FOCUSED, {
                    customerCode: col.customerCode
                  })
                }}
                className='rp-cell-input text-xs'
                style={{ cursor: isReadOnly ? 'default' : 'text' }}
              />
            ) : (
              col.label
            )}

            {filterField && (
              <button
                ref={(element) => { filterButtonRefs.current[filterField] = element }}
                type='button'
                disabled={isFilterLoading}
                className={`column-filter-button ${filterValues.length ? 'active' : ''}`}
                title={`${col.label}で絞り込む`}
                aria-label={`${col.label}フィルター`}
                aria-expanded={openFilter === filterField}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setOpenFilter((current) => current === filterField ? null : filterField)
                }}
              >
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M4 5h16l-6.5 7.2V18l-3 1v-6.8L4 5z' />
                </svg>
              </button>
            )}
            {filterField && openFilter === filterField && filterButtonRefs.current[filterField] && (
              <ColumnValueFilter
                label={col.label}
                values={filterValues}
                options={filterOptions}
                onApply={filterField === 'grade' ? onGradeFilterChange : onSizeFilterChange}
                onClose={() => setOpenFilter(null)}
                anchor={filterButtonRefs.current[filterField]}
                isLoading={isFilterLoading}
              />
            )}

            {!isReadOnly && !col.fixed && (
              <div
                className='resize-handle'
                onMouseDown={(e) => handleMouseDown(e, col.id)}
              />
            )}

            {hiddenAfter.length > 0 && (
              <div
                className='absolute right-0 top-0 bottom-0 w-2 flex items-center justify-center bg-[#106EBE] hover:bg-blue-400 transition-colors z-20 cursor-pointer'
                onClick={(e) => {
                  e.stopPropagation()
                  if (isReadOnly) return
                  hiddenAfter.forEach((k) => onUnhideItem('column', k))
                }}
                title='隠れている列を表示'
              >
                <svg
                  width='8'
                  height='8'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M13 7l5 5-5 5M6 7l5 5-5 5' />
                </svg>
              </div>
            )}
          </th>
        )
      })}
    </>
  )
}

export default ColumnHeader
